import type { NostrEvent } from '@nostrify/nostrify';

export interface Fundraiser {
  event: NostrEvent;
  id: string;
  pubkey: string;
  title: string; // First line of content
  summary?: string;
  content: string;
  goal: number; // in millisats (NIP-75 uses millisats)
  image?: string;
  closedAt?: number;
  references: string[];
  hashtags: string[];
  relays: string[];
  createdAt: number;
}

export interface FundraiserProgress {
  totalRaised: number; // in sats
  zapCount: number;
  percentComplete: number;
  isComplete: boolean;
  isClosed: boolean;
}

export interface Supporter {
  pubkey: string;
  totalSats: number;
  zapCount: number;
  lastZappedAt: number;
}

export function parseFundraiser(event: NostrEvent): Fundraiser | null {
  if (event.kind !== 9041) return null;

  // Required: amount tag (in millisats)
  const amountStr = event.tags.find(([name]) => name === 'amount')?.[1];
  if (!amountStr) return null;

  const amount = parseInt(amountStr);
  if (isNaN(amount) || amount <= 0) {
    return null;
  }

  // Required: relays tag
  const relaysTag = event.tags.find(([name]) => name === 'relays');
  const relays = relaysTag ? relaysTag.slice(1) : [];
  
  if (relays.length === 0) return null;

  // Optional fields
  const summary = event.tags.find(([name]) => name === 'summary')?.[1];
  const image = event.tags.find(([name]) => name === 'image')?.[1];
  const closedAtStr = event.tags.find(([name]) => name === 'closed_at')?.[1];

  const references = event.tags
    .filter(([name]) => name === 'r')
    .map(([_, url]) => url)
    .filter(Boolean);

  const hashtags = event.tags
    .filter(([name]) => name === 't')
    .map(([_, tag]) => tag)
    .filter(Boolean);

  const closedAt = closedAtStr ? parseInt(closedAtStr) : undefined;

  // Use first line of content as title, rest as description
  const lines = event.content.split('\n');
  const title = lines[0] || 'Untitled Goal';

  return {
    event,
    id: event.id,
    pubkey: event.pubkey,
    title,
    summary,
    content: event.content,
    goal: amount, // Store in millisats as per NIP-75
    image,
    closedAt,
    references,
    hashtags,
    relays,
    createdAt: event.created_at,
  };
}
